import moment from "moment";
import "moment/locale/es";

moment.locale("es");

// Redondeo a 2 decimales seguro (evita errores de flotantes)
export function round2(n) {
  return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
}

// Mapa de meses en español (abreviado y completo) -> número de mes (0-11).
// Los estados de cuenta mexicanos usan "ENE", "ENE.", "Ene", "Enero", etc.
const MESES = {
  ene: 0, enero: 0,
  feb: 1, febrero: 1,
  mar: 2, marzo: 2,
  abr: 3, abril: 3,
  may: 4, mayo: 4,
  jun: 5, junio: 5,
  jul: 6, julio: 6,
  ago: 7, agosto: 7,
  sep: 8, sept: 8, septiembre: 8,
  oct: 9, octubre: 9,
  nov: 10, noviembre: 10,
  dic: 11, diciembre: 11,
};

/**
 * Construye un moment a partir de día, texto de mes (es) y año (2 ó 4 dígitos).
 * Útil para fechas de encabezado tipo "01/Marzo/2026" o "01 al 31 de AGOSTO 2021".
 * Devuelve un moment válido o null.
 */
export function fechaDesdePartes(dia, mesTxt, anioRaw) {
  const d = parseInt(dia, 10);
  const key = String(mesTxt || "").toLowerCase().replace(/\./g, "");
  const mes = MESES[key] != null ? MESES[key] : MESES[key.slice(0, 3)];
  if (mes == null || !(d >= 1 && d <= 31)) return null;
  let anio = parseInt(anioRaw, 10);
  if (anioRaw != null && String(anioRaw).trim().length === 2) anio = 2000 + anio;
  if (!anio) anio = moment().year();
  const m = moment({ year: anio, month: mes, day: d });
  return m.isValid() ? m : null;
}

/**
 * Convierte un texto de monto a número.
 * Acepta: "$1,234.56", "1,234.56", "1234.56", "(1,234.56)" (negativo), "1,234.56-".
 * Devuelve null si no logra interpretar un número.
 */
export function parsearMonto(texto) {
  if (texto == null) return null;
  let s = String(texto).trim();
  if (!s) return null;

  // Negativo si viene entre paréntesis o con signo menos al inicio/final
  const negativo = /^\(.*\)$/.test(s) || /^-/.test(s) || /-\s*$/.test(s);

  s = s.replace(/[()$\s]/g, ""); // quita paréntesis, signo de pesos y espacios
  s = s.replace(/,/g, ""); // quita separador de miles
  s = s.replace(/[^0-9.]/g, ""); // deja sólo dígitos y punto decimal

  if (s === "" || s === ".") return null;
  const n = parseFloat(s);
  if (isNaN(n)) return null;

  return negativo ? -Math.abs(n) : round2(n);
}

// Regex reutilizable para localizar montos dentro de una línea ("$1,234.56", "1,234.56", etc.)
export const REGEX_MONTO = /-?\(?\$?\s?\d{1,3}(?:,\d{3})*\.\d{2}\)?/g;

/**
 * Devuelve todos los montos encontrados en una línea como números.
 */
export function montosEnLinea(linea) {
  const matches = String(linea).match(REGEX_MONTO) || [];
  return matches.map(parsearMonto).filter((m) => m != null);
}

/**
 * Intenta parsear una fecha con varios formatos.
 * - formatos: array de formatos moment a intentar (ej. ["DD/MMM", "DD/MM/YYYY"]).
 * - anioRef: año a usar cuando el formato no incluye año.
 * Devuelve un moment válido o null.
 */
export function parsearFecha(texto, anioRef, formatos) {
  if (texto == null) return null;
  const limpio = String(texto).trim();
  if (!limpio) return null;

  const fs = formatos || [
    "DD/MMM",
    "DD-MMM",
    "DD MMM",
    "DD/MM/YYYY",
    "DD/MM/YY",
    "DD-MM-YYYY",
    "DD-MM-YY",
  ];

  for (const f of fs) {
    const m = moment(limpio, f, "es", true);
    if (m.isValid()) {
      if (!/Y/.test(f) && anioRef) m.year(anioRef);
      return m;
    }
  }

  // Respaldo manual: "DD MMM" / "DD/MMM" con meses en español sin punto/acento
  const match = limpio.toLowerCase().match(/(\d{1,2})[\s/.-]+([a-záéíóú]{3,})/);
  if (match) {
    const dia = parseInt(match[1], 10);
    const mesTxt = match[2].replace(/\./g, "").slice(0, 4);
    const mes =
      MESES[mesTxt] != null ? MESES[mesTxt] : MESES[mesTxt.slice(0, 3)];
    if (mes != null && dia >= 1 && dia <= 31) {
      const m = moment({ year: anioRef || moment().year(), month: mes, day: dia });
      if (m.isValid()) return m;
    }
  }

  return null;
}

/**
 * Detecta devoluciones (retiros que el banco regresó a la cuenta) según las reglas
 * `reversos` de la plantilla:
 *   - montoNegativo: regex sobre `raw`; el banco pone el importe en la columna de
 *     retiros con signo negativo ("11,000.00-").
 *   - rastreo: regex con la clave de rastreo en el grupo 1; liga la devolución con
 *     el retiro original (ambos traen la misma clave).
 * Marca cada movimiento con `esReverso` y `rastreo`, y luego los liga (ligarReversos).
 */
export function detectarReversos(movimientos, reglas) {
  if (!reglas) return movimientos;
  movimientos.forEach((mov) => {
    const raw = mov.raw || "";
    mov.esReverso = !!reglas.montoNegativo && reglas.montoNegativo.test(raw);
    const m = reglas.rastreo ? `${raw} ${mov.rawExtra || ""}`.match(reglas.rastreo) : null;
    mov.rastreo = m ? m[1].toUpperCase() : null;
  });
  return ligarReversos(movimientos);
}

// Días máximos entre el retiro y su devolución cuando no hay clave de rastreo.
const DIAS_REVERSO_SIN_RASTREO = 3;

/**
 * Liga cada devolución con el retiro que regresa: mismo monto, naturaleza contraria
 * y anterior a ella. Primero por clave de rastreo; si alguno de los dos no la trae,
 * el retiro más cercano hacia atrás dentro de DIAS_REVERSO_SIN_RASTREO.
 * La devolución queda con `reversoDe` (el original) y el original con `reversado`.
 */
export function ligarReversos(movimientos) {
  movimientos.forEach((dev, i) => {
    if (!dev.esReverso) return;
    const previos = movimientos
      .slice(0, i)
      .reverse()
      .filter(
        (o) =>
          !o.esReverso &&
          !o.reversado &&
          round2(o.monto) === round2(dev.monto) &&
          (!o.tipo || !dev.tipo || o.tipo !== dev.tipo)
      );
    const original =
      (dev.rastreo && previos.find((o) => o.rastreo === dev.rastreo)) ||
      previos.find(
        (o) =>
          (!o.rastreo || !dev.rastreo) &&
          (!o.fecha || !dev.fecha ||
            Math.abs(moment(dev.fecha).diff(moment(o.fecha), "days")) <= DIAS_REVERSO_SIN_RASTREO)
      );
    if (original) {
      original.reversado = true;
      dev.reversoDe = original;
    }
  });
  return movimientos;
}

// Número de página al final de una línea de pie ("2/26", "Página 2 de 26").
const RE_NUM_PAGINA = /\s*(?:p[aá]g(?:ina)?\.?\s*)?\d+\s*(?:\/|de)\s*\d+\s*$/i;
const normalizarLineaPagina = (linea) =>
  String(linea || "").replace(RE_NUM_PAGINA, "").replace(/\s+/g, " ").trim().toLowerCase();

/**
 * Filtro de encabezados y pies de página que se cuelan entre los renglones de
 * detalle cuando un movimiento queda al final de una hoja. Una línea es "de página"
 * si ya apareció antes del primer movimiento (encabezados y pies se repiten en cada
 * hoja), si es sólo el número de página, si es el título "(continuación)" de la
 * tabla, o si coincide con `excluir` (títulos propios del banco).
 *   lineas: todas las líneas del PDF; idxPrimerMovimiento: índice del primer movimiento.
 */
export function crearFiltroPagina(lineas, idxPrimerMovimiento, excluir) {
  const vistas = new Set(
    (idxPrimerMovimiento > 0 ? lineas.slice(0, idxPrimerMovimiento) : []).map(normalizarLineaPagina)
  );
  return (linea) => {
    const n = normalizarLineaPagina(linea);
    return !n || vistas.has(n) || /continuaci[oó]n/i.test(linea) || (!!excluir && excluir.test(linea));
  };
}

// Tope de renglones de detalle por movimiento: si el fin de la sección no se
// detecta bien, evita arrastrar el resumen del estado de cuenta a la descripción.
const MAX_RENGLONES_DETALLE = 8;

/**
 * Descripción completa de un movimiento (`detalle`, la que se ve en las cards):
 * el concepto más sus renglones de detalle (ordenante, beneficiario, clave de
 * rastreo...). Quita renglones repetidos (algunos bancos duplican la descripción).
 * `concepto` no se toca: es parte de la clave con que se guardan las relaciones manuales.
 */
export function armarDetalle(partes) {
  const vistas = new Set();
  const unicas = [];
  (partes || []).forEach((p) => {
    const limpia = limpiarConcepto(p);
    const clave = limpia.toLowerCase();
    if (!limpia || vistas.has(clave)) return;
    vistas.add(clave);
    unicas.push(limpia);
  });
  return unicas.slice(0, MAX_RENGLONES_DETALLE).join(" ");
}

/**
 * Quita los últimos `n` importes de un texto (importe + saldo al final de un bloque)
 * junto con un "-" pegado, para dejar sólo la descripción.
 */
export function quitarUltimosMontos(texto, n) {
  const t = String(texto || "");
  const re = new RegExp(REGEX_MONTO.source + "\\s?-?(?=\\s|$)", "g");
  const pos = [];
  let m;
  while ((m = re.exec(t)) !== null) pos.push([m.index, m.index + m[0].length]);
  return pos
    .slice(-n)
    .reverse()
    .reduce((acc, [ini, fin]) => acc.slice(0, ini) + acc.slice(fin), t);
}

export function limpiarConcepto(texto) {
  return String(texto || "")
    .replace(/\s+/g, " ")
    .trim();
}
